import { useQuery } from '@tanstack/react-query';
import { APITypes } from '../../primary-record-types';
import { getIntl } from '../../utils/intl';
import { getDateLabsCacheKey } from './cacheKeys';
import { useQueryErrorNotify } from '../useQueryCallbacks';
import urls from '../../utils/urls';
import { appAPI } from '../../utils/api';

const intl = getIntl('use-date-labs', {
    loadError: 'Error occurred loading labs',
});

export type UseDateLabsArgs = {
    medicalProfileId: string;
    date: string;
    isDisabled?: boolean;
};

export type UseDateLabsReturnValue = {
    isLoadingDateLabs: boolean;
    dateLabs: APITypes.medicalDataService.GetPanelsForDateResponse;
};

export const useDateLabs = ({ medicalProfileId, date, isDisabled }: UseDateLabsArgs): UseDateLabsReturnValue => {
    const dateLabsQuery = useQuery<APITypes.medicalDataService.GetPanelsForDateResponse>({
        queryKey: getDateLabsCacheKey(medicalProfileId, date),
        queryFn: async (): Promise<APITypes.medicalDataService.GetPanelsForDateResponse> => {
            const request: APITypes.medicalDataService.GetPanelsForDateRequest = {
                date: date,
            };
            const response = await appAPI.post<APITypes.medicalDataService.GetPanelsForDateResponse>(
                urls.api.medicalData.dateLabs(medicalProfileId),
                {
                    body: request,
                },
            );
            return response;
        },
        enabled: !isDisabled,
        staleTime: 1000 * 60 * 5,
    });

    useQueryErrorNotify(dateLabsQuery, intl('loadError'));

    return {
        isLoadingDateLabs: dateLabsQuery.isLoading,
        dateLabs: dateLabsQuery.data || { panels: [], unmatchedLabs: [] },
    };
};
